import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnInit,
  resource,
  signal,
  viewChild,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Analytics } from '@core/services/analytics';
import { ApiClient } from '@core/services/api-client';
import { ProctoringService } from '@core/services/proctoring';
import { Badge } from '@shared/components/badge/badge';
import { Button } from '@shared/components/button/button';
import { Modal } from '@shared/components/modal/modal';
import { Timer, TimerConfig } from '@shared/components/timer/timer';
import { CheckEmailResponse } from '@shared/models/invitation';
import { Problem } from '@shared/models/problem';

@Component({
  selector: 'app-all-problems-page',
  imports: [Button, Timer, Badge, Modal],
  templateUrl: './all-problems-page.html',
})
export class AllProblemsPage implements AfterViewInit, OnInit, OnDestroy {
  private readonly apiClient = inject(ApiClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly analytics = inject(Analytics);
  private readonly proctor = inject(ProctoringService);

  private testSetId = this.route.snapshot.paramMap.get('testSetId');
  private stored = localStorage.getItem('inviteData');
  private redirected = false;
  submitting = signal(false);
  private invitedToken = signal<string>('');
  private currentProblemId: string | null = null;
  private currentPage: 'all_problems' | 'problem' | null = null;
  private currentTestSetId: string | null = null;

  setCurrentProblemId(id: string | null) {
    this.currentProblemId = id;
  }
  setContext(page: 'all_problems' | 'problem', testSetId?: string | null) {
    this.currentPage = page;
    this.currentTestSetId = testSetId ?? null;
  }

  readonly submitModal = viewChild.required('submitModal', { read: Modal });

  async convertParamsToLocalStorage() {
    const stored = localStorage.getItem('inviteData');
    const email = this.route.snapshot.queryParamMap.get('email') || '';
    const invitedToken =
      this.route.snapshot.queryParamMap.get('invitedToken') || '';

    if (!stored) {
      if (!email || !invitedToken) {
        console.error('Missing email or invitedToken');
        this.redirected = true;
        this.router.navigate(['/forbidden']);
        return;
      }

      try {
        const res = (await this.apiClient.checkEmail({
          invitedEmail: email,
          invitedToken,
        })) as CheckEmailResponse;

        const newInviteData = {
          testSetId: res.data.testSetId,
          expiredAt: res.data.expiredAt,
          email,
          invitedToken,
        };

        localStorage.setItem(
          'inviteData',
          JSON.stringify({ data: newInviteData })
        );

        this.invitedToken.set(newInviteData.invitedToken);
        this.email.set(newInviteData.email);
        this.testSetData.reload();
      } catch (err) {
        console.error('checkEmail failed:', err);
        this.redirected = true;
        this.clearLocalStorage();
        this.router.navigate(['/forbidden']);
      }
    } else {
      try {
        const parsed = JSON.parse(stored);
        this.invitedToken.set(parsed.data?.invitedToken || '');
        this.email.set(parsed.data?.email || '');
      } catch {
        this.invitedToken.set('');
        this.email.set('');
      }
    }
  }

  private email = signal<string>(
    (() => {
      if (this.stored) {
        try {
          const parsed = JSON.parse(this.stored);
          return parsed?.data?.email;
        } catch {
          console.error('Failed to parse inviteData from localStorage');
        }
      } else {
        const email = this.route.snapshot.queryParamMap.get('email') || 'test';
        return email;
      }
    })()
  );

  private testSetData = resource({
    loader: async () => {
      const token = this.invitedToken();
      if (!token) return Promise.resolve(null);

      try {
        return await this.apiClient.getProblemsByTestSet(
          this.testSetId!,
          token
        );
      } catch (error) {
        if ((error as HttpErrorResponse).status === 401) {
          console.error('Unauthorized access - invalid token');
          this.clearLocalStorage();
          this.router.navigate(['/forbidden']);
        }
        throw error;
      }
    },
  });
  loading = computed(
    () => !this.invitedToken() || this.testSetData.status() === 'loading'
  );
  problemsData = computed(() => this.testSetData.value()?.problems ?? []);

  expiredAt = computed(() => {
    const dateStr = this.testSetData.value()?.expiredAt;
    if (!dateStr) throw new Error('expiredAt is missing');
    return new Date(dateStr);
  });

  completedAt = computed(() => this.testSetData.value()?.completedAt ?? null);

  timerConfig = computed<TimerConfig>(() => ({
    type: 'date',
    expireAt: this.expiredAt(),
    onExpire: () => this.router.navigate(['/exam/complete']),
  }));

  getButtonState(problem: Problem) {
    if (this.loading()) return 'loading';

    const storedData = localStorage.getItem('eachProblemResult');
    if (!storedData) return 'start';

    try {
      const problemResults: { problemId: string; deadline: string }[] =
        JSON.parse(storedData);
      const problemData = problemResults.find(
        p => p.problemId === problem.problemId
      );

      if (!problemData) return 'start';

      const deadline = new Date(problemData.deadline);
      const now = new Date();

      if (now > deadline) return 'ended';
      return 'continue';
    } catch {
      console.error('Failed to parse eachProblemResult');
      return 'start';
    }
  }

  getButtonLabel(problem: Problem) {
    switch (this.getButtonState(problem)) {
      case 'loading':
        return 'Loading...';
      case 'continue':
        return 'Continue';
      case 'ended':
        return 'Ended';
      default:
        return 'Start';
    }
  }

  isButtonDisabled(problem: Problem) {
    const state = this.getButtonState(problem);
    return state === 'loading' || state === 'ended';
  }

  private clearLocalStorage() {
    // Track user sign out before clearing storage
    this.analytics.trackUserSignOut();
    // Clear all exam-related data
    localStorage.removeItem('inviteData');
    localStorage.removeItem('eachProblemResult');
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}
  private trackingReady = false;
  async ngOnInit() {
    await this.convertParamsToLocalStorage();
    this.testSetData.reload();
  }
  async ngAfterViewInit() {
    await this.convertParamsToLocalStorage();
    for (let i = 0; i < 20; i++) {
      // ~2s max
      if (this.testSetData.status() !== 'loading') break;
      await new Promise(r => setTimeout(r, 100));
    }
    const data = this.testSetData.value();
    this.analytics.trackExamStarted();
    if (!this.redirected) {
      if (data?.completedAt || data?.expiredAt === null) {
        this.redirected = true;
        this.clearLocalStorage();
        this.router.navigate(['/exam/complete']);
      } else if (!this.invitedToken()) {
        this.redirected = true;
        console.error('No invitedToken found');
        this.router.navigate(['/forbidden']);
      }
    }
    //โหลด Clarity/GA4 เฉพาะตอนอยู่ใน /exam และ session valid
    if (!this.trackingReady) {
      await this.analytics.configureUserTracking();
      // ตั้งค่า context ของ proctoring สำหรับหน้านี้
      this.proctor.setContext('all_problems', this.testSetId || null);
      this.proctor.setCurrentProblemId(null);

      this.proctor.start();
      this.trackingReady = true;
    }
    this.analytics.trackExamStarted();
  }
  ngOnDestroy() {
    this.proctor.stop();
  }

  startTest(problem: Problem) {
    if (this.isButtonDisabled(problem)) return;

    if (!this.trackingReady) {
      throw new Error('analytics is not ready');
    }
    // ตั้ง context ว่ากำลังจะไปหน้า problem อะไร (ช่วยให้ event chain ต่อเนื่อง)
    this.proctor.setContext('problem', this.testSetId || null);
    this.proctor.setCurrentProblemId(problem.problemId);
    // Track problem start
    this.analytics.trackExamEvent('problem_started', {
      email: this.email(),
      problem_id: problem.problemId,
      test_set_id: this.testSetId,
      problem_state: this.getButtonState(problem),
    });

    this.router.navigate(['problem', problem.problemId], {
      relativeTo: this.route,
    });
  }

  async forceSubmitAll() {
    this.submitting.set(true);
    const body = {
      invitedToken: this.invitedToken(),
    };
    try {
      await this.apiClient.submission(body);

      // Track forced submission (time up)
      this.analytics.trackExamEvent('exam_force_submitted', {
        email: this.email(),
        test_set_id: this.testSetId,
        submission_type: 'time_expired',
        submitAt: new Date().toISOString(),
      });

      if (!this.redirected) {
        this.redirected = true;
        this.clearLocalStorage();
        this.router.navigate(['/exam/complete']);
      }
    } catch {
      console.error('Submission failed');
    } finally {
      this.submitting.set(false);
    }
  }

  async handleSubmitAll() {
    this.submitModal().open();
  }

  async SubmitAll() {
    this.submitting.set(true);
    const body = {
      invitedToken: this.invitedToken(),
    };
    try {
      await this.apiClient.submission(body);

      // Track manual submission
      this.analytics.trackExamEvent('exam_manually_submitted', {
        email: this.email(),
        test_set_id: this.testSetId,
        submission_type: 'user_initiated',
        submitAt: new Date().toISOString(),
      });

      if (!this.redirected) {
        this.redirected = true;
        this.clearLocalStorage();
        this.router.navigate(['/exam/complete']);
      }
    } catch {
      console.error('Submission failed:');
    } finally {
      this.submitting.set(false);
    }
  }
}
